import { useEffect } from "react";

/**
 * Composant SEO pour gérer les meta tags dynamiques
 * Compatible avec React 19
 */
const SEO = ({
  title = "MSIT Conseil - Expertise en Conseil et Transformation Digitale",
  description = "MSIT Conseil est une société de conseil spécialisée en transformation digitale, intelligence artificielle, data et services technologiques.",
  keywords = "conseil, transformation digitale, intelligence artificielle, data, services technologiques, MSIT",
  image = "https://www.msit-conseil.fr/logo512.png",
  url = "https://www.msit-conseil.fr",
  type = "website",
  noindex = false,
  nofollow = false,
  breadcrumbs = null,
  article = null,
}) => {
  useEffect(() => {
    // Mettre à jour le titre
    document.title = title;

    // Fonction pour mettre à jour ou créer une meta tag
    const updateMetaTag = (name, content, attribute = "name") => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Meta tags de base
    updateMetaTag("description", description);
    updateMetaTag("keywords", keywords);

    // Robots
    const robotsContent = [
      noindex ? "noindex" : "index",
      nofollow ? "nofollow" : "follow",
    ].join(", ");
    updateMetaTag("robots", robotsContent);

    // Open Graph
    updateMetaTag("og:title", title, "property");
    updateMetaTag("og:description", description, "property");
    updateMetaTag("og:image", image, "property");
    updateMetaTag("og:url", url, "property");
    updateMetaTag("og:type", type, "property");
    updateMetaTag("og:site_name", "MSIT Conseil", "property");

    // Twitter Card
    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:title", title);
    updateMetaTag("twitter:description", description);
    updateMetaTag("twitter:image", image);

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // Langue
    const htmlElement = document.documentElement;
    htmlElement.setAttribute("lang", "fr");

    // Fonction pour gérer les scripts JSON-LD
    const updateJSONLD = (id, jsonData) => {
      let script = document.getElementById(id);
      if (!script) {
        script = document.createElement("script");
        script.id = id;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonData);
    };

    // Organization structured data avec logo (pour Google Knowledge Graph)
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "MSIT Conseil",
      "url": "https://www.msit-conseil.fr",
      "logo": "https://www.msit-conseil.fr/logo-msit.png",
      "description": "MSIT Conseil est une société de conseil spécialisée en transformation digitale, intelligence artificielle, data et services technologiques.",
      "sameAs": [
        "https://www.msit-conseil.fr"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "url": "https://www.msit-conseil.fr/contact"
      }
    };
    updateJSONLD("organization-schema", organizationSchema);

    // Breadcrumb structured data (si fourni)
    if (breadcrumbs && Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": crumb.name,
          "item": crumb.url
        }))
      };
      updateJSONLD("breadcrumb-schema", breadcrumbSchema);
    } else {
      // Supprimer le breadcrumb s'il n'est pas fourni
      const breadcrumbScript = document.getElementById("breadcrumb-schema");
      if (breadcrumbScript) {
        breadcrumbScript.remove();
      }
    }

    // Article structured data for blog pages
    if (article) {
      const articleSchema = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": url
        },
        "headline": article.headline || title,
        "description": article.description || description,
        "image": article.image || image,
        "datePublished": article.datePublished,
        "dateModified": article.dateModified || article.datePublished,
        "author": {
          "@type": "Organization",
          "name": article.authorName || "MSIT Conseil",
          "url": "https://www.msit-conseil.fr"
        },
        "publisher": {
          "@type": "Organization",
          "name": "MSIT Conseil",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.msit-conseil.fr/logo-msit.png"
          }
        }
      };
      updateJSONLD("article-schema", articleSchema);
    } else {
      const articleScript = document.getElementById("article-schema");
      if (articleScript) {
        articleScript.remove();
      }
    }

    // Website structured data
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "MSIT Conseil",
      "url": "https://www.msit-conseil.fr",
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://www.msit-conseil.fr/blogs?search={search_term_string}"
        },
        "query-input": "required name=search_term_string"
      }
    };
    updateJSONLD("website-schema", websiteSchema);
  }, [title, description, keywords, image, url, type, noindex, nofollow, breadcrumbs, article]);

  return null;
};

export default SEO;


